import { Hymn } from "@/services/hymnsApi";
import { getHymnSlug } from "@/utils";
import { useEffect, useState, useCallback } from "react";

export interface FavoriteHymn {
  id?: string;
  section_code: string;
  section: string;
  number: number;
  title: string;
  category?: string | null;
  language?: string;
  media_url?: string | null;
  addedAt: number;
}

export const FAVORITES_STORAGE_KEY = "egarizim_favorite_hymns";
export const MAX_FAVORITES = Infinity;
export const FAVORITES_EVENT_NAME = "egarizim_favorites_changed";

/**
 * Retrieve current favorites list from localStorage safely
 */
export function getFavorites(): FavoriteHymn[] {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }
  try {
    const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return [];
  } catch (err) {
    console.warn("Failed to parse favorites from localStorage:", err);
    return [];
  }
}

/**
 * Check if a hymn is in favorites
 */
export function isFavorite(sectionCode: string, number: number): boolean {
  const list = getFavorites();
  return list.some(
    (item) =>
      item.section_code.toUpperCase() === sectionCode.toUpperCase() &&
      item.number === number
  );
}

/**
 * Toggle favorite status for a hymn.
 * When adding, places it at the very top (index 0).
 */
export function toggleFavorite(hymn: Hymn | FavoriteHymn): {
  isFavorited: boolean;
  favorites: FavoriteHymn[];
} {
  const current = getFavorites();
  const existingIndex = current.findIndex(
    (item) =>
      item.section_code.toUpperCase() === hymn.section_code.toUpperCase() &&
      item.number === hymn.number
  );

  let updated: FavoriteHymn[];
  let isFavorited: boolean;

  if (existingIndex !== -1) {
    // Already in favorites -> remove it
    updated = current.filter((_, idx) => idx !== existingIndex);
    isFavorited = false;
  } else {
    // New favorite -> add to front
    const newEntry: FavoriteHymn = {
      id: hymn.id,
      section_code: hymn.section_code,
      section: hymn.section,
      number: hymn.number,
      title: hymn.title,
      category: hymn.category,
      language: hymn.language,
      media_url: hymn.media_url,
      addedAt: Date.now(),
    };
    updated = [newEntry, ...current];
    isFavorited = true;
  }

  saveFavorites(updated);
  return { isFavorited, favorites: updated };
}

/**
 * Remove a specific hymn from favorites
 */
export function removeFavorite(
  sectionCode: string,
  number: number
): FavoriteHymn[] {
  const current = getFavorites();
  const updated = current.filter(
    (item) =>
      !(
        item.section_code.toUpperCase() === sectionCode.toUpperCase() &&
        item.number === number
      )
  );
  saveFavorites(updated);
  return updated;
}

/**
 * Clear all favorites
 */
export function clearFavorites(): void {
  saveFavorites([]);
}

export function saveFavorites(favorites: FavoriteHymn[]): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    // Broadcast change across same window
    window.dispatchEvent(
      new CustomEvent(FAVORITES_EVENT_NAME, { detail: favorites })
    );
  } catch (err) {
    console.error("Failed to save favorites to localStorage:", err);
  }
}

/**
 * React hook to observe and manipulate favorites with auto-sync
 */
export function useFavorites(currentSectionCode?: string, currentNumber?: number) {
  const [favorites, setFavorites] = useState<FavoriteHymn[]>(() => getFavorites());

  const isCurrentFavorited = !!(
    currentSectionCode &&
    currentNumber &&
    favorites.some(
      (f) =>
        f.section_code.toUpperCase() === currentSectionCode.toUpperCase() &&
        f.number === currentNumber
    )
  );

  useEffect(() => {
    // Initial fetch to ensure up-to-date
    setFavorites(getFavorites());

    const handleLocalUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<FavoriteHymn[]>;
      if (customEvent.detail && Array.isArray(customEvent.detail)) {
        setFavorites(customEvent.detail);
      } else {
        setFavorites(getFavorites());
      }
    };

    const handleStorageUpdate = (e: StorageEvent) => {
      if (e.key === FAVORITES_STORAGE_KEY) {
        setFavorites(getFavorites());
      }
    };

    window.addEventListener(FAVORITES_EVENT_NAME, handleLocalUpdate);
    window.addEventListener("storage", handleStorageUpdate);

    return () => {
      window.removeEventListener(FAVORITES_EVENT_NAME, handleLocalUpdate);
      window.removeEventListener("storage", handleStorageUpdate);
    };
  }, []);

  const toggle = useCallback(
    (hymn: Hymn | FavoriteHymn) => {
      return toggleFavorite(hymn);
    },
    []
  );

  const remove = useCallback(
    (sectionCode: string, hymnNumber: number) => {
      return removeFavorite(sectionCode, hymnNumber);
    },
    []
  );

  const clear = useCallback(() => {
    clearFavorites();
  }, []);

  return {
    favorites,
    isCurrentFavorited,
    toggle,
    remove,
    clear,
    maxLimit: MAX_FAVORITES,
  };
}

/**
 * Generate formatted plain-text export for favorites list:
 * - [SECTION_CODE - Section Name] N° [Number] - [Song title]: [egarizim url]
 */
export function generateFavoritesExportText(favorites: FavoriteHymn[]): string {
  const origin = "https://egarizim.com";

  return favorites
    .map((fav) => {
      const slug = getHymnSlug(fav.number, fav.title);
      const url = `${origin}/chants-desperance/${encodeURIComponent(
        fav.section_code
      )}/${slug}`;
      
      const parts = [
        fav.section_code?.toUpperCase(),
        fav.section && fav.section.toLowerCase() !== fav.section_code?.toLowerCase()
          ? fav.section
          : null,
      ].filter(Boolean);

      const sectionBadge = parts.length > 0 ? `[${parts.join(" - ")}] ` : "";
      return `- ${sectionBadge}N° ${fav.number} - ${fav.title}: ${url}`;
    })
    .join("\n");
}

/**
 * Export favorites as a downloadable text file (.txt)
 */
export function exportFavoritesToFile(favorites: FavoriteHymn[]): boolean {
  if (typeof window === "undefined" || favorites.length === 0) return false;
  try {
    const text = generateFavoritesExportText(favorites);
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `egarizim-favoris-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch (err) {
    console.error("Failed to export favorites file:", err);
    return false;
  }
}

/**
 * Export favorites by opening default email client with pre-filled content
 */
export function sendFavoritesByEmail(favorites: FavoriteHymn[]): boolean {
  if (typeof window === "undefined" || favorites.length === 0) return false;
  try {
    const text = generateFavoritesExportText(favorites);
    const origin = "https://egarizim.com";
    const subject = encodeURIComponent("Mes cantiques favoris - Égarizim");
    const body = encodeURIComponent(
      `Bonjour,\n\nVoici ma liste de cantiques favoris sur Égarizim :\n\n${text}\n\nRetrouvez tous les chants sur ${origin}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    return true;
  } catch (err) {
    console.error("Failed to open email client:", err);
    return false;
  }
}

/**
 * Copy formatted favorites text to clipboard
 */
export async function copyFavoritesToClipboard(
  favorites: FavoriteHymn[]
): Promise<boolean> {
  if (typeof window === "undefined" || favorites.length === 0) return false;
  try {
    const text = generateFavoritesExportText(favorites);
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback if clipboard API not available
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    return true;
  } catch (err) {
    console.error("Failed to copy favorites to clipboard:", err);
    return false;
  }
}

// Re-export import utilities for convenience
export * from "./import-favorites";
